import { motion } from 'framer-motion'
import { Transaction } from '@/types'
import { formatCurrency, formatDate } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { 
  ArrowUpRight, 
  ArrowDownLeft, 
  ArrowUp,
  ArrowDown,
  QrCode, 
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  ArrowRight
} from 'lucide-react'

interface TransactionListProps {
  transactions: Transaction[]
  showBalance?: boolean
}

export function TransactionList({ transactions, showBalance = true }: TransactionListProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()

  const getTransactionIcon = (type: string, status: string, direction?: string | null) => {
    if (status === 'pending') return Clock
    if (status === 'failed') return XCircle
    if (status === 'completed') {
      // For transfers/payments, use direction-based icons
      if ((type === 'payment' || type === 'transfer') && direction) {
        return direction === 'sent' ? ArrowDown : ArrowUp
      }
      if (type === 'deposit') return ArrowDownLeft
      if (type === 'withdrawal') return ArrowUpRight
    }
    return CreditCard
  }

  const getTransactionColor = (type: string, status: string, direction?: string | null) => {
    if (status === 'failed') return 'text-error'
    if (status === 'pending') return 'text-warning'
    if (status === 'completed') {
      // For transfers/payments, use direction-based colors
      if ((type === 'payment' || type === 'transfer') && direction) {
        return direction === 'sent' ? 'text-error' : 'text-success'
      }
      if (type === 'deposit') return 'text-success'
      if (type === 'transfer') return 'text-primary'
      if (type === 'payment') return 'text-secondary'
      if (type === 'withdrawal') return 'text-text-primary'
    }
    return 'text-text-secondary'
  }

  const getTransactionBgColor = (type: string, status: string, direction?: string | null) => {
    if (status === 'failed') return 'bg-error/10'
    if (status === 'pending') return 'bg-warning/10'
    if (status === 'completed') {
      // For transfers/payments, use direction-based colors
      if ((type === 'payment' || type === 'transfer') && direction) {
        return direction === 'sent' ? 'bg-error/10' : 'bg-success/10'
      }
      if (type === 'deposit') return 'bg-success/10'
      if (type === 'transfer') return 'bg-primary/10'
      if (type === 'payment') return 'bg-secondary/10'
      if (type === 'withdrawal') return 'bg-gray-100'
    }
    return 'bg-gray-100'
  }

  const getTransactionIconColor = (type: string, status: string, direction?: string | null) => {
    if (status === 'failed') return 'text-error'
    if (status === 'pending') return 'text-warning'
    if (status === 'completed') {
      // For transfers/payments, use direction-based colors
      if ((type === 'payment' || type === 'transfer') && direction) {
        return direction === 'sent' ? 'text-error' : 'text-success'
      }
      if (type === 'deposit') return 'text-success'
      if (type === 'transfer') return 'text-primary'
      if (type === 'payment') return 'text-secondary'
      if (type === 'withdrawal') return 'text-text-primary'
    }
    return 'text-text-secondary'
  }

  const getTransactionLabel = (type: string) => {
    switch (type) {
      case 'payment': return t('wallet.payment') || 'Pagamento'
      case 'transfer': return t('wallet.transfer') || 'Transferência'
      case 'deposit': return t('wallet.deposit') || 'Depósito'
      case 'withdrawal': return t('wallet.withdraw') || 'Saque'
      default: return t('wallet.transaction') || 'Transação'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return t('wallet.pending') || 'Pendente'
      case 'completed': return t('wallet.completed') || 'Concluída'
      case 'failed': return t('wallet.failed') || 'Falhou'
      case 'cancelled': return t('wallet.cancelled') || 'Cancelada'
      default: return status
    }
  }

  const getCurrencyName = (currency: string) => {
    switch (currency) {
      case 'STN': return 'Dobra'
      case 'EUR': return 'Euro'
      case 'USD': return 'Dólar'
      default: return currency
    }
  }

  // Helper to determine if transaction was sent or received
  const getTransactionDirection = (transaction: Transaction) => {
    if (!user) return null
    
    // Check if current user is sender (compare by email)
    const isSender = transaction.from_wallet_user === user.email
    
    // Check if current user is receiver (compare by email)
    const isReceiver = transaction.to_wallet_user === user.email
    
    if (isSender && isReceiver) return null // Same user (shouldn't happen)
    if (isSender) return 'sent'
    if (isReceiver) return 'received'
    return null
  }
  
  // Helper to get the other user's name
  const getOtherUserName = (transaction: Transaction) => {
    if (!user) return null
    
    const isSender = transaction.from_wallet_user === user.email
    
    if (isSender) {
      // User sent, so show receiver name
      return transaction.to_wallet_user_name || transaction.to_wallet_user || 'Unknown'
    } else {
      // User received, so show sender name
      return transaction.from_wallet_user_name || transaction.from_wallet_user || 'Unknown'
    }
  }

  return (
    <div className="space-y-3">
      {transactions.map((transaction, index) => {
        const direction = getTransactionDirection(transaction)
        const otherUserName = getOtherUserName(transaction)
        const IconComponent = getTransactionIcon(transaction.transaction_type, transaction.status, direction)
        const colorClass = getTransactionColor(transaction.transaction_type, transaction.status, direction)
        
        return (
          <motion.div
            key={transaction.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              getTransactionBgColor(transaction.transaction_type, transaction.status, direction)
            }`}>
              <IconComponent className={`h-5 w-5 ${
                getTransactionIconColor(transaction.transaction_type, transaction.status, direction)
              }`} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  {/* Description */}
                  <h3 className="font-medium text-text-primary truncate">
                    {transaction.description || getTransactionLabel(transaction.transaction_type)}
                  </h3>
                  
                  {/* User name and direction */}
                  {direction && otherUserName && (
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs text-text-secondary">
                        {direction === 'sent' ? t('common.to') : t('common.from')}: {otherUserName}
                      </span>
                    </div>
                  )}
                  
                  {/* Show wallet info for P2P transfers */}
                  {transaction.transaction_type === 'P2P' && (transaction.from_wallet || transaction.to_wallet) && (
                    <div className="flex items-center space-x-2 mt-1">
                      {transaction.from_wallet && (
                        <>
                          <span className="text-xs text-text-secondary">
                            {getCurrencyName(transaction.from_wallet.currency || transaction.currency)}
                          </span>
                          <ArrowRight className="h-3 w-3 text-text-tertiary" />
                        </>
                      )}
                      {transaction.to_wallet && (
                        <span className="text-xs text-text-secondary">
                          {getCurrencyName(transaction.to_wallet.currency || transaction.currency)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <span className={`text-sm font-semibold ml-2 ${colorClass}`}>
                  {transaction.transaction_type === 'CASH_IN'
                    ? '+'
                    : transaction.transaction_type === 'CASH_OUT'
                    ? '-'
                    : direction === 'sent'
                    ? '-'
                    : direction === 'received'
                    ? '+'
                    : ''}
                  {showBalance ? formatCurrency(transaction.amount, transaction.currency_code || transaction.currency) : '••••••'}
                </span>
              </div>
              
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-text-secondary">
                    {getTransactionLabel(transaction.transaction_type)}
                  </span>
                  {transaction.metadata?.qr_code_id && (
                    <QrCode className="h-3 w-3 text-primary" />
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    transaction.status === 'completed' 
                      ? 'bg-success/10 text-success'
                      : transaction.status === 'pending'
                      ? 'bg-warning/10 text-warning'
                      : 'bg-error/10 text-error'
                  }`}>
                    {getStatusLabel(transaction.status)}
                  </span>
                  <span className="text-xs text-text-secondary">
                    {formatDate(transaction.created_at)}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )
      })}
    </div>
  )
}















